import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppGlobals } from '@app/app.global';
import { EntityService } from '@app/core/services';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Interface for DMart Order
 */
export interface DmartOrder {
  orderId: string;
  parentOrderId: string;
  orderDate: string;
  orderPlacedDate: string;
  orderStatus: string;
  orderStatusText: string;
  orderItemCount: string;
  deliveryMode: string;
  totalProductPrice: string;
  totalShippingCharge: string;
  totalSavings: string;
  totalSalesTax: string;
  grandTotal: string;
  digitalInvoiceUrl: string;
  paymentType: string;
  shippingAddress?: any;
  shipments?: any[];
}

/**
 * Interface for DMart Orders API Response
 */
export interface DmartOrdersResponse {
  totalRecords: number;
  orderList: DmartOrder[];
}

/**
 * Service for managing DMart order operations
 * Handles order fetching, pagination, and search
 */
@Injectable({
  providedIn: 'root'
})
export class DmartOrderService {

  constructor(
    private http: HttpClient,
    private entityService: EntityService,
    private appGlobals: AppGlobals
  ) { }

  /**
   * Get HTTP headers with entity ID
   */
  private getHeaders(): HttpHeaders {
    const currentEntity = this.entityService.getSingleSelectedEntity();
    const entityId = currentEntity?.id || (typeof currentEntity === 'string' ? currentEntity : '');

    return new HttpHeaders({
      'x-entity-ids': entityId || ''
    });
  }

  /**
   * Fetch DMart orders from the API
   * @param params Query parameters for pagination and search
   * @returns Observable of DMart orders response
   */
  getOrders(params: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Observable<any> {
    const headers = this.getHeaders();
    
    // Build query parameters
    let httpParams = new HttpParams();
    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.pageSize) {
      httpParams = httpParams.set('pageSize', params.pageSize.toString());
    }
    if (params.search && params.search.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    const url = `${this.appGlobals.apiUrl.dashboard}dmart/orders`;

    return this.http.get<DmartOrdersResponse>(url, { headers, params: httpParams })
      .pipe(
        map(response => this.mapDmartResponse(response)),
        catchError(error => {
          console.error('Error fetching DMart orders:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Map DMart API response to a consistent format for UI consumption
   * @param response Raw API response
   * @returns Mapped response with pagination and order data
   */
  private mapDmartResponse(response: DmartOrdersResponse): any {
    const orders = (response.orderList || []).map(order => ({
      orderId: order.orderId,
      parentOrderId: order.parentOrderId,
      orderDate: order.orderDate,
      orderPlacedDate: order.orderPlacedDate,
      orderStatus: order.orderStatus,
      orderStatusText: order.orderStatusText,
      orderItemCount: order.orderItemCount,
      deliveryMode: order.deliveryMode,
      totalProductPrice: this.parseAmount(order.totalProductPrice),
      totalShippingCharge: this.parseAmount(order.totalShippingCharge),
      totalSavings: this.parseAmount(order.totalSavings),
      totalSalesTax: this.parseAmount(order.totalSalesTax),
      grandTotal: this.parseAmount(order.grandTotal),
      digitalInvoiceUrl: order.digitalInvoiceUrl,
      paymentType: order.paymentType,
      shippingAddress: order.shippingAddress,
      shipments: order.shipments
    }));

    return {
      success: true,
      data: {
        orders: orders,
        pagination: {
          totalRecords: response.totalRecords || 0,
          totalPages: Math.ceil((response.totalRecords || 0) / 10)
        }
      }
    };
  }

  /**
   * Parse string amount to number
   * @param amount String amount from API
   * @returns Parsed number or 0
   */
  private parseAmount(amount: string): number {
    if (!amount || amount === '') {
      return 0;
    }
    return parseFloat(amount) || 0;
  }

  /**
   * Download DMart invoice as binary data
   * @param orderId Order ID
   * @returns Observable with blob response
   */
  downloadInvoice(orderId: string): Observable<any> {
    const headers = this.getHeaders();
    const url = `${this.appGlobals.apiUrl.dashboard}dmart/orders/${orderId}/invoice`;

    return this.http.get(url, {
      headers,
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      catchError(error => {
        console.error('Error downloading DMart invoice:', error);
        return throwError(() => error);
      })
    );
  }
}
